/**
 * Author: postcodex
 * @todo define postcodexLookupObj in php wp_localize_script()
 */
import axios from 'axios';
import memoize from 'lodash/memoize';
import template from 'lodash/template';
import isNull from 'lodash/isNull';
import omit from 'lodash/omit';

export const apiRequest = ( props = {} ) => {
	const { ep, options, method } = props;

	return sendRequest( ep, omit( props, ['ep', 'options', 'method'] ), method || 'POST', options || {} );
};

export const sendRequest = ( ep, props = null, method = 'POST', options = {} ) => {
	const defaultArgs = {
		url         : `${ postcodexLookupObj.apiUrl }${ ep }`,
		method      : method || 'POST',
		headers     : { 'X-WP-Nonce' : postcodexLookupObj.restNonce },
		timeout     : 30000,
		dataType    : 'json',
		contentType : 'application/json',
	};

	const args = ( 'get' === method.toLowerCase() )
	             ? { ...defaultArgs, params : props, ...options }
	             : { ...defaultArgs, data : props, ...options };

	return axios( args )
		.then(
			response => {
				return response;
			}
		)
		.catch(
			error => {
				return ( { error } );
			}
		);
};

/**
 * Usage:
 * Somewhere on the page you have the following markup:
 * <script id="tmpl-my-template" type="text/html">
 *     <div class="some-class">
 *         {{ jsVariableNameObject.whatever }}
 *     </div>
 * </script>
 *
 * // then on your component file:
 *
 * // Load the template
 * const myTemplate = _template( 'my-template', 'jsVariableNameObject' );
 *
 * // Output the template passing the variable for output. Example variable object:
 * const someObjectInAppToBeDisplayed = {
 *     whatever : 'Sample Text'
 * };
 * myTemplate( someObjectInAppToBeDisplayed );
 */
export const _template = memoize( function ( id, varName ) {
	const variable = varName || 'data';
	let compiled,
	    options = {
		    evaluate    : /<#([\s\S]+?)#>/g,
		    interpolate : /\{\{\{([\s\S]+?)\}\}\}/g,
		    escape      : /\{\{([^\}]+?)\}\}(?!\})/g,
		    variable    : variable
	    };

	return function ( data ) {
		compiled = compiled || template( document.getElementById( `tmpl-${ id }` ).innerHTML, options );
		return compiled( data );
	};
} );

/**
 * Create EventSource object
 * @param url
 * @returns object
 */
export const createSource = ( url ) => {
	const source = new EventSource( url );
	let deferred;

	source.onmessage = ( event ) => {
		const json = JSON.parse( event.data );

		if ( deferred ) {
			deferred.resolve( json );
			deferred = null;
		}
	};

	source.onopen = ( event ) => {
		console.log( 'eventsource open' );
	};

	source.onerror = ( event ) => {
		const source = event.target;
		console.log( 'eventsource error', event );
		//source.close();
	};

	return {
		nextMessage() {
			if ( !deferred ) {
				deferred = {};
				deferred.promise = new Promise( ( resolve ) => deferred.resolve = resolve );
			}

			return deferred.promise;
		},
		source
	};
};

export const _localStorage = {
	getItem : ( key ) => {
		if ( typeof Storage !== 'undefined' ) {
			return localStorage.getItem( key );
		}

		return null;
	},

	setItem : ( key, value ) => {
		if ( typeof Storage !== 'undefined' ) {
			try {
				localStorage.setItem( key, value );
			} catch ( e ) {
				console.log( e );
			}
		}
	},

	getJSON : ( key ) => {
		if ( typeof Storage !== 'undefined' ) {
			return JSON.parse( localStorage.getItem( key ) );
		}

		return null;
	},

	setJSON : ( key, value ) => {
		if ( typeof Storage !== 'undefined' ) {
			try {
				localStorage.setItem( key, JSON.stringify( value ) );
			} catch ( e ) {
				console.log( e );
			}
		}
	},
};

export const _sessionStorage = {
	get : ( key ) => {
		if ( typeof Storage !== 'undefined' ) {
			return sessionStorage.getItem( key );
		}

		return null;
	},

	set : ( key, value ) => {
		if ( typeof Storage !== 'undefined' ) {
			try {
				sessionStorage.setItem( key, value );
			} catch ( e ) {
				console.log( e );
			}
		}
	},

	getJSON : ( key ) => {
		if ( typeof Storage !== 'undefined' ) {
			return JSON.parse( sessionStorage.getItem( key ) );
		}

		return null;
	},

	setJSON : ( key, value ) => {
		if ( typeof Storage !== 'undefined' ) {
			try {
				sessionStorage.setItem( key, JSON.stringify( value ) );
			} catch ( e ) {
				console.log( e );
			}
		}
	},
};

/**
 * Usage:
 * hashCode( data ).then( hash => { ...do something with `hash` } );
 *
 * @param message
 * @param maxChars
 * @returns {Promise<string>}
 */
export const hashCode = async ( message, maxChars = 8 ) => {
	let code = '';
	if ( 'undefined' !== typeof crypto && 'undefined' !== typeof crypto.subtle && 'undefined' !== typeof crypto.subtle.digest ) {
		const msgUint8   = new TextEncoder().encode( JSON.stringify( message ) ),
		      hashBuffer = await crypto.subtle.digest( 'SHA-256', msgUint8 ),
		      hashArray  = Array.from( new Uint8Array( hashBuffer ) );

		code = hashArray.map( b => b.toString( 16 ).padStart( 2, '0' ) ).join( '' );
	} else {
		code = md5( JSON.stringify( message ) );
	}

	if ( isNull( maxChars ) && maxChars !== code.length ) {
		return code;
	}

	return code.substr( 0, maxChars );
};
